import {
  Box,
  Pagination,
  PaginationItem,
  Stack,
  SvgIcon,
  styled
} from '@mui/material';
import Paper from '@/components/Paper';
import Input from '@/components/Input';
import InputAdornment from '@/components/InputAdornment';
import useResponsive from '@/hooks/useResponsive';
import Typography from '@/components/Typography';
import chunk from 'lodash/chunk';
import { useMemo, useState } from 'react';
import Row from '@/components/TablePrice/Row';
import { ArrowLeft2, ArrowRight2 } from 'iconsax-react';
import useGetCrypto, {
  CryptoType,
  ascending,
  descending
} from '@/hooks/useGetCrypto';
import handleSearch from '@/utils/handleSearch';
import CustomColumn, { Column, sortType } from './Column';

const Tab = (props: {
  selected: boolean;
  value: 'p2p' | 'otc';
  label: string;
  onClick: (key: 'p2p' | 'otc') => void;
}) => {
  const { value, label, onClick, selected } = props;
  return (
    <Box
      p={3}
      onClick={() => onClick(value)}
      sx={theme => ({
        position: 'relative',
        cursor: 'pointer',
        '&:before': {
          left: 0,
          right: 0,
          content: "''",
          position: 'absolute',
          bottom: 0,
          width: '100%',
          height: '4px',
          borderRadius: 4,
          backgroundColor: selected ? theme.palette.primary.A100 : 'transparent'
        }
      })}
    >
      <Typography variant="body1">{label}</Typography>
    </Box>
  );
};

type Row = string | JSX.Element;

const Header = styled(Box)(({ theme }) => ({
  display: 'grid',
  height: 70,
  backgroundColor: '#fff',
  borderRadius: 8,
  padding: theme.spacing(2, 6),
  border: `1px solid ${theme.palette.secondary.A100}`
}));

const CustomRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  height: 70,
  borderRadius: 8,
  backgroundColor: '#fff',
  gap: '4px',
  padding: theme.spacing(2, 5),
  border: `1px solid ${theme.palette.secondary.A100}`
}));

const PAGE_LIMIT = 15;
const AssetsList = () => {
  const { matches } = useResponsive();
  const [activeTab, setActiveTab] = useState<'p2p' | 'otc'>('otc');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ value: sortType; key: string } | null>(
    null
  );

  const { p2p: p2pList, otc: otcList } = useGetCrypto(
    {
      p2p: { side: 'p2p', quote: 'TMN' },
      otc: { side: 'otc', quote: 'TMN' }
    },
    'getCryptoList',
    []
  );

  const list = useMemo(
    () => (activeTab === 'p2p' ? p2pList : otcList),
    [activeTab]
  );

  const sortedList = useMemo(() => {
    const sortMap = { ascending, descending };
    const newList = sort
      ? sortMap[sort.value](list, sort.key, activeTab)
      : list;
    return newList;
  }, [sort]);

  const areas = matches({
    xs:
      activeTab === 'p2p'
        ? "'crypto closePrice changePrice'"
        : "'crypto buyPrice sellPrice'",
    lg:
      activeTab === 'p2p'
        ? "'crypto closePrice changePrice volume chart action'"
        : "'crypto buyPrice sellPrice changePrice chart action'"
  });
  const columns = matches({
    xs: xsColumns[activeTab],
    lg: lgColumns[activeTab]
  });

  const gridTemplateColumns = columns.map((c: Column) => c.span).join(' ');

  const handleSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(1);
    setSearchQuery(event.target.value);
  };

  const handlePagination = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleTabs = (key: 'p2p' | 'otc') => {
    setActiveTab(key);
    setPage(1);
    setSearchQuery('');
  };

  const newList = useMemo(
    () => chunk(handleSearch(sortedList, searchQuery), PAGE_LIMIT) || [],
    [sortedList, searchQuery, sort]
  );

  const hasItem = newList.length > 0;

  return (
    <Stack gap={1.5} mb={{ xs: 13, lg: 18 }}>
      <Paper
        elevation={0}
        sx={{ borderRadius: 3, px: matches({ md: 4, lg: 6 }), pt: 8, pb: 6 }}
      >
        <Stack gap={1} mb={{ xs: 2, lg: 6 }}>
          <Typography
            textAlign="center"
            variant={matches({ md: 'subtitle', lg: 'h6' })}
            component="p"
            weight="bold"
          >
            قیمت لحظه‌ای بازارهای تومانی ارزهای دیجیتال
          </Typography>
        </Stack>
        <Stack
          gap={2}
          flexDirection={{ xs: 'column', lg: 'row' }}
          alignItems={{ xs: 'center', lg: 'center' }}
          justifyContent={{ xs: 'start', lg: 'space-between' }}
        >
          <Stack
            display={{ xs: 'flex', lg: 'none' }}
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            mb={matches({ md: 4, lg: 0 })}
          >
            <Tab
              selected={activeTab === 'otc'}
              label="تبدیل سریع"
              value="otc"
              onClick={handleTabs}
            />
            <Tab
              selected={activeTab === 'p2p'}
              label="معامله ‌حرفه‌ای"
              value="p2p"
              onClick={handleTabs}
            />
          </Stack>
          <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            mb={0}
            gap={3}
            display={{ xs: 'none', lg: 'flex' }}
          >
            <Box
              borderRadius={3}
              sx={theme => ({
                cursor: 'pointer',
                backgroundColor:
                  activeTab === 'otc'
                    ? theme.palette.secondary.A80
                    : 'transparent'
              })}
            >
              <Typography
                py={3}
                px={6}
                variant="button"
                display="block"
                onClick={() => handleTabs('otc')}
              >
                تبدیل سریع
              </Typography>
            </Box>
            <Box
              borderRadius={3}
              sx={theme => ({
                cursor: 'pointer',
                backgroundColor:
                  activeTab === 'p2p'
                    ? theme.palette.secondary.A80
                    : 'transparent'
              })}
            >
              <Typography
                py={3}
                px={6}
                variant="button"
                display="block"
                onClick={() => handleTabs('p2p')}
              >
                معامله ‌حرفه‌ای
              </Typography>
            </Box>
          </Stack>
          <Input
            value={searchQuery}
            sx={{
              width: matches({ md: '100%', lg: 335 }),
              borderRadius: 3,
              height: matches({ md: 'unset', lg: 56 })
            }}
            hasBg
            placeholder="جست‌و‌جوی بازار ..."
            onChange={handleSearchInput}
            startAdornment={
              <InputAdornment position="start">
                <SvgIcon
                  sx={theme => ({
                    fontSize: '24px',
                    color: theme.palette.grayBackground.A100
                  })}
                >
                  <path
                    d="M9.58268 17.5013C13.9549 17.5013 17.4993 13.9569 17.4993 9.58464C17.4993 5.21238 13.9549 1.66797 9.58268 1.66797C5.21043 1.66797 1.66602 5.21238 1.66602 9.58464C1.66602 13.9569 5.21043 17.5013 9.58268 17.5013Z"
                    stroke="currentColor"
                    fill="transparent"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M18.3327 18.3346L16.666 16.668"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </SvgIcon>
              </InputAdornment>
            }
            {...(!!searchQuery && {
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSearchQuery('')}
                >
                  <SvgIcon
                    sx={theme => ({
                      fontSize: '16px',
                      color: theme.palette.grayBackground.A100
                    })}
                  >
                    <path
                      d="M18 6L6 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </SvgIcon>
                </InputAdornment>
              )
            })}
            disableUnderline
          />
        </Stack>
      </Paper>
      <Header style={{ gridTemplateColumns }}>
        {columns.map((column: Column, i: number) => (
          <CustomColumn
            key={i}
            title={column.title}
            secondaryTitle={column.secondaryTitle}
            align={column.align}
            padding={column.padding}
            sortKey={column.sortKey}
            onClick={val => setSort(val)}
          />
        ))}
      </Header>
      <Stack gap={1}>
        {(hasItem ? newList[page - 1] : []).map((item: CryptoType) => (
          <CustomRow
            key={item[activeTab]?.id}
            style={{
              gridTemplateColumns,
              gridTemplateAreas: areas ? areas : 'unset'
            }}
          >
            <Row
              pingPong={false}
              source={activeTab}
              symbol={item.symbol}
              {...matches({
                xs: {
                  ...(activeTab === 'otc'
                    ? {
                        sellPrice: item[activeTab]?.sellPrice,
                        buyPrice: item[activeTab]?.buyPrice
                      }
                    : {
                        closePrice: item[activeTab]?.price,
                        changePrice: item[activeTab]?.priceChange
                      }),
                  crypto: {
                    logo: item.logoUrl,
                    nameFa: item.baseFa,
                    symbol: item.base
                  },
                  onClickRedirect: `/price/${item.base}`
                },
                lg: {
                  crypto: {
                    logo: item.logoUrl,
                    nameFa: item.baseFa,
                    symbol: item.base
                  },
                  ...(activeTab === 'otc'
                    ? {
                        sellPrice: item[activeTab]?.sellPrice,
                        buyPrice: item[activeTab]?.buyPrice
                      }
                    : {
                        closePrice: item[activeTab]?.price,
                        volume: item[activeTab]?.volume
                      }),
                  changePrice: item[activeTab]?.priceChange,
                  chart: item.chartUrl,
                  onClickRedirect: `/price/${item.base}`,
                  button: {
                    secondary: `/price/${item.base}_price`, // show price link
                    text: activeTab === 'p2p' ? 'معامله' : 'خرید و فروش',
                    href: 'https://p.rabex.ir/'
                  }
                }
              })}
            />
          </CustomRow>
        ))}
      </Stack>
      <Stack alignItems="center" mt={matches({ md: 6, lg: 18 })}>
        {hasItem && (
          <Pagination
            renderItem={item => (
              <PaginationItem
                slots={{ previous: ArrowLeft2, next: ArrowRight2 }}
                {...item}
              />
            )}
            count={newList.length}
            color="primary"
            page={page}
            onChange={handlePagination}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default AssetsList;

const xsColumns = {
  p2p: [
    {
      title: 'بازار',
      align: 'start',
      span: '2fr'
    },
    {
      title: 'آخرین قیمت',
      secondaryTitle: '(تومان)',
      align: 'start',
      span: '2fr'
    },
    {
      title: 'درصد تغییر',
      secondaryTitle: '(۲۴h)',
      align: 'end',
      span: '2fr'
    }
  ],
  otc: [
    {
      title: 'بازار',
      align: 'start',
      span: '2fr'
    },
    {
      title: 'قیمت خرید',
      secondaryTitle: '(تومان)',
      align: 'start',
      span: '2fr'
    },
    {
      title: 'قیمت فروش',
      secondaryTitle: '(تومان)',
      align: 'end',
      span: '2fr'
    }
  ]
};

const lgColumns = {
  p2p: [
    {
      title: 'بازار',
      span: '2.5fr',
      align: 'start'
    },
    {
      title: 'آخرین قیمت',
      secondaryTitle: '(تومان)',
      span: '2fr',
      align: 'start',
      sortKey: 'price'
    },
    {
      title: 'درصد تغییر',
      secondaryTitle: '(۲۴ ساعته)',
      span: '2fr',
      align: 'start',
      sortKey: 'priceChange'
    },
    {
      title: 'حجم معاملات',
      secondaryTitle: '(۲۴ ساعته)',
      span: '2fr',
      align: 'start',
      sortKey: 'volume'
    },
    {
      title: 'نمودار هفتگی',
      span: '2fr'
    },
    {
      title: '',
      align: 'end',
      span: '2fr'
    }
  ],
  otc: [
    {
      title: 'بازار',
      span: '2.5fr',
      align: 'start'
    },
    {
      title: 'قیمت خرید',
      secondaryTitle: '(تومان)',
      span: '2fr',
      align: 'start',
      sortKey: 'buyPrice'
    },
    {
      title: 'قیمت فروش',
      secondaryTitle: '(تومان)',
      span: '2fr',
      align: 'start',
      sortKey: 'sellPrice'
    },
    {
      title: 'درصد تغییر',
      secondaryTitle: '(۲۴ ساعته)',
      span: '2fr',
      align: 'start',
      sortKey: 'priceChange'
    },
    {
      title: 'نمودار هفتگی',
      span: '2fr'
    },
    {
      title: '',
      align: 'end',
      span: '2fr'
    }
  ]
};
